import React, { Component } from 'react';
import './TimeSeriesGraph.css';
import { d3 } from 'react-d3-components';
import moment from 'moment';
import { t } from 'i18next';

class TimeSeriesGraph extends Component {
  componentDidMount() {
    this.buildGraph();
  }

  componentWillReceiveProps(props) {
    this.updateGraph(props.data, props);
  }

  buildGraph() {
    const {
      data,
      width = this.timeSeriesGraph.offsetWidth,
      height = 300,
      propertyText,
      colors = {
      },
      stdeviation,
      yAxisTitle,
    } = this.props;

    const margin = {
      bottom: 80,
      left: 60,
      right: 80,
      top: 40,
    };

    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    const x = d3.time.scale()
      .range([30, chartWidth])
      .domain([
        d3.min(data, d => d.x),
        d3.max(data, d => d.x)]);
    const y = d3.scale.linear()
      .range([chartHeight, 0])
      .domain([
        d3.min(data, d => (Math.min(d.y1, d.y2) || (d.y1 || d.y2)) - d.padding),
        d3.max(data, d => (Math.max(d.y1, d.y2) || (d.y1 || d.y2)) + d.padding)]);

    const xAxis = d3.svg.axis().scale(x).orient('bottom')
      .innerTickSize(-chartHeight)
      .outerTickSize(0)
      .tickPadding(10)
      .tickFormat(d3.time.format('%b %d, %Y %H:%M'));

    const yAxis = d3.svg.axis().scale(y).orient('left')
      .innerTickSize(-chartWidth)
      .outerTickSize(0)
      .tickPadding(10);

    const svg = d3.select(this.timeSeriesGraph).append('svg')
      .attr('width', width - 50)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    this.setState({
      svg,
    }, () => {
      this.addAxes(xAxis, yAxis, margin, chartWidth, chartHeight, yAxisTitle);
      this.drawPaths(x, y, data, colors);
      if (stdeviation) this.drawAnomalies(x, y, data, propertyText, colors);
      this.appendLegend(chartWidth);
    });
  }

  addAxes(xAxis, yAxis, margin, chartWidth, chartHeight, yAxisTitle) {
    const { svg } = this.state;

    const axes = svg.append('g')
      .attr('class', 'axies');

    axes.append('g')
      .attr('class', 'x axis')
      .attr('transform', `translate(0,${chartHeight})`)
      .call(xAxis)
      .selectAll('text')
      .attr('transform', 'rotate(25)')
      .style('text-anchor', 'start')
      .append('text')
      .attr('y', -6)
      .attr('x', chartWidth)
      .style('text-anchor', 'end')
      .text('Time');

    axes.append('g')
      .attr('class', 'y axis')
      .attr('transform', 'translate(30,0)')
      .call(yAxis)
      .append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', -75)
      .attr('dy', '.71em')
      .style('text-anchor', 'end')
      .text(yAxisTitle);
  }

  drawPaths(x, y, data, colors) {
    const { svg } = this.state;
    const {
      areaColor, meanColor, transformColor
    } = colors;
    const line1 = d3.svg.line()
      .x(d => x(d.x))
      .y(d => y(d.y1));

    const line2 = d3.svg.line()
      .x(d => x(d.x))
      .y(d => y(d.y2));

    const area = d3.svg.area()
      .x(d => x(d.x))
      .y0(d => y(d.y1 + (d.padding || 0)))
      .y1(d => y(d.y1 - (d.padding || 0)));

    const graph = svg.append('g')
      .attr('class', 'graph');

    graph.datum(data);

    graph.append('path')
      .attr('class', 'area')
      .style('stroke', areaColor)
      .style('fill', areaColor)
      .attr('d', area);

    graph.append('path')
      .attr('class', 'line transform')
      .style('stroke', transformColor)
      // .style('fill', transformColor)
      .attr('d', line1);

    graph.append('path')
      .attr('class', 'line mean')
      .style('stroke', meanColor)
      .style('fill', meanColor)
      .attr('d', line2);
  }

  drawAnomalies(x, y, data, propertyText, { anomalyColor }) {
    const { svg } = this.state;
    const { stdeviation = 1 } = this.props;

    const tooltip = d3.select('.timeSeriesSpace')
      .append('div')
      .attr('id', 'tooltip')
      .style('opacity', 0);

    const anomaliesData = data.filter(datapoint => Math.abs(datapoint.std) > Math.abs(stdeviation));

    const anomalies = svg.append('g')
      .attr('class', 'anomalies');

    anomalies
      .selectAll('.anomaly')
      .data(anomaliesData)
      .enter()
      .append('circle')
      .attr('class', 'anomaly')
      .style('fill', anomalyColor)
      .style('stroke', 'black')
      .style('stroke-width', '0.5px')
      .attr('r', 5)
      .attr('cx', d => x(d.x))
      .attr('cy', d => y(d.y1))
      .on('mouseover', (d) => {
        tooltip.transition()
          .duration(200)
          .style('opacity', 0.9);
        tooltip.html(`
            ${t('tensorAnalytics.graphs.timeSeriesGraph.tooltipDetected')}:
            ${moment(d.x).format('MM/DD/YYYY HH:mm:ss')}<br>
            ${t('tensorAnalytics.graphs.timeSeriesGraph.tooltipProperty')}: ${propertyText}<br>
            ${t('tensorAnalytics.graphs.timeSeriesGraph.tooltipSTD')}: ${d.std.toFixed(2)}<br>
            ${t('tensorAnalytics.graphs.timeSeriesGraph.tooltipRaw')}: ${d.raw.toFixed(2)}
          `)
          .style('left', `${d3.event.pageX - 80}px`)
          .style('top', `${d3.event.pageY - 70}px`);
      })
      .on('mouseout', () => {
        tooltip.transition()
          .duration(500)
          .style('opacity', 0);
      });
  }

  appendLegend(chartWidth) {
    const { svg } = this.state;
    const {
      colors, labels, stdeviation
    } = this.props;
    const {
      anomalyColor, areaColor, meanColor, transformColor
    } = colors;
    const {
      anomalyLabel, areaLabel, meanLabel, transformLabel
    } = labels;

    const legend = svg.append('g')
      .attr('class', 'chartLegend');

    const legendRow = legend.append('g')
      .attr('class', 'legendRow');

    if (stdeviation) {
      legendRow.append('circle')
        .attr('r', 5)
        .attr('dy', '.32em')
        .style('fill', anomalyColor)
        .style('stroke', anomalyColor);

      legendRow.append('text')
        .attr('dy', '.32em')
        .text(anomalyLabel)
        .attr('x', '10');
    }

    legendRow.append('rect')
      .attr('width', 12)
      .attr('height', 4)
      .attr('y', '-2px')
      .style('fill', areaColor)
      .style('stroke', areaColor)
      .attr('x', '130px');

    legendRow.append('text')
      .text(areaLabel)
      .attr('dy', '.32em')
      .attr('x', '145px');

    legendRow.append('rect')
      .attr('width', 12)
      .attr('height', 4)
      .attr('y', '-2px')
      .style('fill', meanColor)
      .style('stroke', meanColor)
      .attr('x', '270px');

    legendRow.append('text')
      .text(meanLabel)
      .attr('dy', '.32em')
      .attr('x', '285px');

    legendRow.append('rect')
      .attr('width', 12)
      .attr('height', 4)
      .attr('y', '-2px')
      .style('fill', transformColor)
      .style('stroke', transformColor)
      .attr('x', '325px');

    legendRow.append('text')
      .text(transformLabel)
      .attr('dy', '.32em')
      .attr('x', '340px');

    const legendBox = legend.select('.legendRow').node().getBBox();

    legend.attr('transform', `translate(${chartWidth - legendBox.width - legendBox.x}, -20)`);
  }

  updateGraph(data, props) {
    const {
      width = this.timeSeriesGraph.offsetWidth,
      height = 300,
      stdeviation,
      propertyText,
    } = props;

    const margin = {
      bottom: 80,
      left: 60,
      right: 80,
      top: 40,
    };

    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    const x = d3.time.scale()
      .range([30, chartWidth])
      .domain([
        d3.min(data, d => d.x),
        d3.max(data, d => d.x)]);
    const y = d3.scale.linear()
      .range([chartHeight, 0])
      .domain([
        d3.min(data, d => (Math.min(d.y1, d.y2) || (d.y1 || d.y2)) - d.padding),
        d3.max(data, d => (Math.max(d.y1, d.y2) || (d.y1 || d.y2)) + d.padding)]);

    const xAxis = d3.svg.axis().scale(x).orient('bottom')
      .innerTickSize(-chartHeight)
      .outerTickSize(0)
      .tickPadding(10)
      .tickFormat(d3.time.format('%b %d, %Y %H:%M'));

    const yAxis = d3.svg.axis().scale(y).orient('left')
      .innerTickSize(-chartWidth)
      .outerTickSize(0)
      .tickPadding(10);

    this.updatePaths(x, y, data);
    this.updateAxes(xAxis, yAxis, margin, chartWidth, chartHeight);
    if (stdeviation) this.updateAnomalies(x, y, data, props, propertyText);
  }

  updatePaths(x, y, data) {
    const { svg } = this.state;

    const line1 = d3.svg.line()
      .x(d => x(d.x))
      .y(d => y(d.y1));

    const line2 = d3.svg.line()
      .x(d => x(d.x))
      .y(d => y(d.y2));

    const area = d3.svg.area()
      .x(d => x(d.x))
      .y0(d => y(d.y1 + (d.padding || 0)))
      .y1(d => y(d.y1 - (d.padding || 0)));

    const graph = svg.select('g.graph')
      .attr('class', 'graph');

    graph.datum(data);

    graph.select('.area')
      .attr('d', area);

    graph.select('.transform')
      .attr('d', line1);
    graph.select('.mean')
      .attr('d', line2);
  }

  updateAxes(xAxis, yAxis) {
    const { svg } = this.state;

    const axes = svg.select('.axies');

    axes.select('.x.axis')
      .call(xAxis)
      .selectAll('text')
      .attr('transform', 'rotate(25)')
      .style('text-anchor', 'start');

    axes.select('.y.axis')
      .call(yAxis);
  }

  updateAnomalies(x, y, data, props, propertyText) {
    const { svg } = this.state;
    const { stdeviation = 1, colors: { anomalyColor } } = props;

    const tooltip = d3.select('#tooltip');

    const anomaliesData = data.filter(datapoint => Math.abs(datapoint.std) > Math.abs(stdeviation));

    const anomalies = svg.select('.anomalies');

    anomalies
      .selectAll('.anomaly')
      .remove();

    anomalies
      .selectAll('.anomaly')
      .data(anomaliesData)
      .enter()
      .append('circle')
      .style('fill', anomalyColor)
      .style('stroke', 'black')
      .style('stroke-width', '0.5px')
      .attr('class', 'anomaly')
      .attr('r', 5)
      .attr('cx', d => x(d.x))
      .attr('cy', d => y(d.y1))
      .on('mouseover', (d) => {
        tooltip.transition()
          .duration(200)
          .style('opacity', 0.95);
        tooltip.html(`
            ${t('tensorAnalytics.graphs.timeSeriesGraph.tooltipDetected')}:
            ${moment(d.x).format('MM/DD/YYYY HH:mm:ss')}<br>
            ${t('tensorAnalytics.graphs.timeSeriesGraph.tooltipProperty')}: ${propertyText}<br>
            ${t('tensorAnalytics.graphs.timeSeriesGraph.tooltipSTD')}: ${d.std.toFixed(2)}<br>
            ${t('tensorAnalytics.graphs.timeSeriesGraph.tooltipRaw')}: ${d.raw.toFixed(2)}
          `)
          .style('left', `${d3.event.pageX - 80}px`)
          .style('top', `${d3.event.pageY - 65}px`);
      })
      .on('mouseout', () => {
        tooltip.transition()
          .duration(500)
          .style('opacity', 0);
      });
  }

  render() {
    return (
      <div className="timeSeriesSpace"
        ref={(c) => { this.timeSeriesGraph = c; }}
      />
    );
  }
}

export default TimeSeriesGraph;
